import {
  isMascotCrowned,
  MASCOT_AURA_FRAME_COUNT,
  type MascotLook,
  type MascotSpriteState,
  type MascotStage,
} from "~/components/mascot/launcher/mascot-sprite.model";

export type MascotAnimation = "idle" | "greet" | "sleep" | "notify" | "laugh" | "evolve";
export type MascotEvolution = { fromStage: MascotStage; toStage: MascotStage };

export const MASCOT_ANIMATION_DURATIONS_MS = { greet: 600, notify: 1400, laugh: 1600, evolve: 2300 } as const;
const EVOLUTION_FLICKER_MS = 1600;
const BLINK_PERIOD_MS = 3800;
const BLINK_DURATION_MS = 140;
const AURA_FRAME_MS = 110;

export const mascotStageFromFullTimeEquivalent = (fullTimeEquivalent: number): MascotStage => {
  if (fullTimeEquivalent < 10) return 1;
  if (fullTimeEquivalent < 20) return 2;
  if (fullTimeEquivalent < 30) return 3;
  return 4;
};

export const mascotStageFromStorage = (storedStage: number): MascotStage | null => {
  if (storedStage === 1 || storedStage === 2 || storedStage === 3 || storedStage === 4) return storedStage;
  return null;
};

export const mascotEvolutionFromStages = (
  lastSeenStage: MascotStage | null,
  stage: MascotStage
): MascotEvolution | null => {
  if (lastSeenStage === null || stage <= lastSeenStage) return null;
  return { fromStage: lastSeenStage, toStage: stage };
};

export const mascotLookFromPointer = (params: {
  pointerX: number;
  launcherLeft: number;
  launcherWidth: number;
}): MascotLook => {
  const distanceFromCenter = params.pointerX - (params.launcherLeft + params.launcherWidth / 2);
  if (Math.abs(distanceFromCenter) < params.launcherWidth / 2) return 0;
  return distanceFromCenter < 0 ? -1 : 1;
};

export const mascotSpriteState = (params: {
  animation: MascotAnimation;
  stage: MascotStage;
  evolution: MascotEvolution | null;
  elapsedMs: number;
  nowMs: number;
  look: MascotLook;
  reducedMotion: boolean;
}): MascotSpriteState => {
  const isCrowned = isMascotCrowned(params.stage);
  const isBlinking = !params.reducedMotion && params.nowMs % BLINK_PERIOD_MS < BLINK_DURATION_MS;
  const state: MascotSpriteState = {
    stage: params.stage,
    eyes: isBlinking ? "closed" : "open",
    mouth: isCrowned ? "smirk" : "smile",
    look: params.look,
    silhouette: false,
    auraFrame: params.reducedMotion ? 0 : Math.floor(params.nowMs / AURA_FRAME_MS) % MASCOT_AURA_FRAME_COUNT,
  };

  switch (params.animation) {
    case "idle":
      return state;
    case "greet":
      return { ...state, eyes: "laugh" };
    case "sleep":
      return { ...state, eyes: "closed", mouth: "none", look: 0 };
    case "notify":
      return { ...state, eyes: "wide", mouth: "o", look: 0 };
    case "laugh":
      return { ...state, eyes: "laugh", mouth: Math.floor(params.nowMs / 110) % 2 ? "open" : "grin", look: 0 };
    case "evolve": {
      if (!params.evolution) return state;
      const progress = params.elapsedMs / EVOLUTION_FLICKER_MS;
      // Silhouettes of both stages alternate faster and faster, like a Pokémon evolution.
      if (progress < 1) {
        const showsNextStage = Math.floor(progress * progress * 18) % 2 === 1;
        return {
          ...state,
          stage: showsNextStage ? params.evolution.toStage : params.evolution.fromStage,
          silhouette: true,
          look: 0,
        };
      }
      return {
        ...state,
        stage: params.evolution.toStage,
        eyes: "laugh",
        mouth: isMascotCrowned(params.evolution.toStage) ? "smirk" : "smile",
        look: 0,
      };
    }
  }
};
